// @flow
import * as React from 'react';

import { localize } from '@ta/common.i18n';
import { Modal } from '@ta/overlays.modal';
import { Closer, Opener } from '@ta/overlays.managers';
import { TitleBar } from '@ta/modal.headers';
import Button from '@ta/styleguide.button';
import { useSocialLoginGate } from '@ta/social.login-gate';

import styles from './ContentModals.less';
import ReportLinkPanel from './content/Links';
import ReportReviewPanel from './content/Reviews';
import ReportPhotoPanel from './content/Photos';
import ReportVideoPanel from './content/Videos';
import ReportForumPanel from './content/Forums';
import ReportTripPanel from './content/Trips';
import ReportLocationTopicPanel from './content/LocationTopic';
import ReportLocationTopicPostPanel from './content/LocationTopicPost';
import ReportMemberPanel from './content/Member';

import type { ContentIdType } from './content/base';
import type { LocationInfo } from './content/Reviews';
import type { ForumInfo, RepostInfo } from './submit/SubmitIapVote';

const OKCloser = () => (
  <Closer>
    {close => (
      <Button size="fullwidth" type="primary" onClick={close}>
        {localize('common_OK')}
      </Button>
    )}
  </Closer>
);

const ReportVoteThankYou = () => (
  <div className={styles.iapTYContent}>
    <p className={styles.iapTYH1} style={{ textAlign: 'center' }}>
      {localize('social_report_ty2')}
    </p>
    <p className={styles.iapTYText}>{localize('social_report_ty2b')}</p>
    {OKCloser()}
  </div>
);

const ReportVoteError = () => (
  <div className={`${styles.iapTYContent} ${styles.iapTYText}`}>
    {localize('social_report_prof_err')}
    {OKCloser()}
  </div>
);

type ReportState = {|
  status: 'panel' | 'thankyou' | 'error',
|};

type ReportProps = {|
  modalName: string,
  children: (onError: () => void, onComplete: () => void) => React.Node,
|};

/**
 * Generic modal for reporting - accepts a function that takes the onError and onComplete methods
 * and returns the appropriate reporting panel.
 */
class ReportModal extends React.Component<ReportProps, ReportState> {
  state = {
    status: 'panel',
  };

  onError = () => {
    this.setState({ status: 'error' });
  };

  onComplete = () => {
    this.setState({ status: 'thankyou' });
  };

  render = () => {
    let content;
    if (this.state.status === 'thankyou') {
      content = <ReportVoteThankYou />;
    } else if (this.state.status === 'error') {
      content = <ReportVoteError />;
    } else {
      content = (
        <div className={styles.iapModalContent}>
          {this.props.children(this.onError, this.onComplete)}
        </div>
      );
    }

    const header = (
      <TitleBar iconName="flag-fill" displayType="LEFT_ALIGN_GRAY">
        <div id="ReportIAP">{localize('report_problem_ffffff85')}</div>
      </TitleBar>
    );

    return (
      <Modal name={this.props.modalName} header={header}>
        {content}
      </Modal>
    );
  };
}

type OpenModalProps = {|
  name: string,
  /** A render prop that gives you an open function */
  children: (open: () => void) => React.Node,
  /** Product ID */
  pid?: number,
|};

export const ReportModalOpener = ({ name, children, pid }: OpenModalProps) => {
  const socialGate = useSocialLoginGate({ pid });
  return (
    <Opener name={name}>
      {open =>
        children(() => {
          socialGate().then(open);
        })
      }
    </Opener>
  );
};

type ModalProps = {|
  contentId: ContentIdType,
  modalName: string,
  location?: LocationInfo,
  forumInfo?: ForumInfo,
  repostInfo?: RepostInfo,
|};

const DefaultProps = {
  repostInfo: undefined,
  forumInfo: undefined,
  location: undefined,
};

/**
 * Create a reported link modal
 */
const ReportLinkModal = (props: ModalProps) => (
  <ReportModal modalName={props.modalName}>
    {(onError, onComplete) => (
      <ReportLinkPanel
        onError={onError}
        onComplete={onComplete}
        contentId={props.contentId}
        repostInfo={props.repostInfo}
      />
    )}
  </ReportModal>
);
ReportLinkModal.defaultProps = DefaultProps;

const ReportReviewModal = (props: ModalProps) => (
  <ReportModal modalName={props.modalName}>
    {(onError, onComplete) => (
      <ReportReviewPanel
        onError={onError}
        onComplete={onComplete}
        contentId={props.contentId}
        location={props.location}
        repostInfo={props.repostInfo}
      />
    )}
  </ReportModal>
);
ReportReviewModal.defaultProps = DefaultProps;

const ReportPhotoModal = (props: ModalProps) => (
  <ReportModal modalName={props.modalName}>
    {(onError, onComplete) => (
      <ReportPhotoPanel
        onError={onError}
        onComplete={onComplete}
        contentId={props.contentId}
        repostInfo={props.repostInfo}
      />
    )}
  </ReportModal>
);
ReportPhotoModal.defaultProps = DefaultProps;

const ReportVideoModal = (props: ModalProps) => (
  <ReportModal modalName={props.modalName}>
    {(onError, onComplete) => (
      <ReportVideoPanel
        onError={onError}
        onComplete={onComplete}
        contentId={props.contentId}
        repostInfo={props.repostInfo}
      />
    )}
  </ReportModal>
);
ReportVideoModal.defaultProps = DefaultProps;

const ReportForumModal = (props: ModalProps) => (
  <ReportModal modalName={props.modalName}>
    {(onError, onComplete) => (
      <ReportForumPanel
        onError={onError}
        onComplete={onComplete}
        contentId={props.contentId}
        forumInfo={props.forumInfo}
        repostInfo={props.repostInfo}
      />
    )}
  </ReportModal>
);
ReportForumModal.defaultProps = DefaultProps;

const ReportTripModal = (props: ModalProps) => (
  <ReportModal modalName={props.modalName}>
    {(onError, onComplete) => (
      <ReportTripPanel
        onError={onError}
        onComplete={onComplete}
        contentId={props.contentId}
        repostInfo={props.repostInfo}
      />
    )}
  </ReportModal>
);
ReportTripModal.defaultProps = DefaultProps;

const ReportLocationTopicModal = (props: ModalProps) => (
  <ReportModal modalName={props.modalName}>
    {(onError, onComplete) => (
      <ReportLocationTopicPanel
        onError={onError}
        onComplete={onComplete}
        contentId={props.contentId}
        repostInfo={props.repostInfo}
      />
    )}
  </ReportModal>
);

ReportLocationTopicModal.defaultProps = DefaultProps;

const ReportLocationTopicPostModal = (props: ModalProps) => (
  <ReportModal modalName={props.modalName}>
    {(onError, onComplete) => (
      <ReportLocationTopicPostPanel
        onError={onError}
        onComplete={onComplete}
        contentId={props.contentId}
        repostInfo={props.repostInfo}
      />
    )}
  </ReportModal>
);

ReportLocationTopicPostModal.defaultProps = DefaultProps;

const ReportMemberModal = (props: ModalProps) => (
  <ReportModal modalName={props.modalName}>
    {(onError, onComplete) => (
      <ReportMemberPanel onError={onError} onComplete={onComplete} contentId={props.contentId} />
    )}
  </ReportModal>
);
ReportMemberModal.defaultProps = DefaultProps;

const ReportRepostModal = (props: ModalProps) => {
  const { originalType = undefined } = props.repostInfo || {};
  switch (originalType) {
    case 'Review':
      return <ReportReviewModal {...props} />;
    case 'Photo':
      return <ReportPhotoModal {...props} />;
    case 'Video':
      return <ReportVideoModal {...props} />;
    case 'LinkPost':
      return <ReportLinkModal {...props} />;
    case 'ForumPost':
      return <ReportForumModal {...props} />;
    case 'Trip':
      return <ReportTripModal {...props} />;
    case 'ApiLocationTopic':
      return <ReportLocationTopicModal {...props} />;
    case 'ApiTopicPosting':
      return <ReportLocationTopicPostModal {...props} />;
    default:
      return null;
  }
};
ReportRepostModal.defaultProps = DefaultProps;

export {
  ReportLinkModal,
  ReportReviewModal,
  ReportPhotoModal,
  ReportVideoModal,
  ReportForumModal,
  ReportTripModal,
  ReportRepostModal,
  ReportLocationTopicModal,
  ReportLocationTopicPostModal,
  ReportMemberModal,
  ReportVoteError,
  ReportVoteThankYou,
};
